import styled, { css } from 'styled-components'
import { media, sizes } from 'modules/common/components/ui/utils'
import colors from 'modules/common/components/ui/colors'
import isInteger from 'lodash/isInteger'

// custom cols for our theme inspired by https://github.com/LoicMahieu/react-styled-flexboxgrid
const THEME_CONF = 'btgrid'
const BASE_CONF = {
  gridSize: 12,
  gutterWidth: 1,
  outerMargin: 2,
  mediaQuery: 'only screen',
  breakpoints: {
    xs: 0,
    s: (320/16),
    sm: (668/16),
    md: (768/16),
    lg: (1024/16),
    xl: (1280/16),
    xxl: (1366/16),
    xxxl: (1440/16),
  }
}

const DIMENSION_NAMES = ['xs', 's', 'sm', 'md', 'lg', 'xl', 'xxl', 'xxxl']
const offsetProps = DIMENSION_NAMES.map(d => d + 'Offset')


function config (props) {
  const cacheId = makeCacheId(props)
  if (configCache[0] === cacheId) {
    return configCache[1]
  }
  const conf = resolveConfig(props)
  configCache[0] = cacheId
  configCache[1] = conf
  return conf
}
  
  function makeMedia (media) {
    return (...args) => css`
      @media ${media} {
        ${css(...args)}
      }
    `
  }
  
  
const configCache = []
const makeCacheId = props => JSON.stringify((props.theme && props.theme[THEME_CONF]) || {})
const resolveConfig = props => {
  const themeConf = (props.theme && props.theme[THEME_CONF]) || {}

  const conf = {
    ...BASE_CONF,
    ...themeConf,
    container: {
      ...BASE_CONF.container,
      ...themeConf.container
    },
    breakpoints: {
      ...BASE_CONF.breakpoints,
      ...themeConf.breakpoints
    }
  }

  conf.media = Object.keys(conf.breakpoints).reduce((media, breakpoint) => {
    const breakpointWidth = conf.breakpoints[breakpoint]
    media[breakpoint] = makeMedia(
      [
        conf.mediaQuery,
        breakpoint !== 0 && `(min-width: ${breakpointWidth}em)`
      ]
        .filter(Boolean)
        .join(' and ')
    )
    return media
  }, {})

  return conf
}


export const PageArticle = styled.article`
  max-width: 1120px;
  margin: auto;
  ${media.minWidth(sizes.tabletWide)`
    padding-left: 10px;
    padding-right: 10px;
  `}
`

export const PageHeader = styled.header`
  width: 100%;
  flex-basis: 100%;
  text-align: center;
  padding: 1rem 10px 1rem;
  border-bottom: 1px solid ${colors.BORDER_COLOR};
  .stretch {
    margin-left: -10px;
    margin-right: -10px;
  }
  ${media.minWidth(sizes.tabletWide)`
    width: calc(300rem / 16);
    flex-basis: calc(300rem / 16);
    border-bottom: none;
    border-right: 1px solid ${colors.BORDER_COLOR};
  `}
  .pro--contact + .pro--course-details,
  .contact + .course-details{
    margin-top: calc(30rem / 16);
  }
  .btn{
    min-width:216px;
  }
  .title--main{
    margin-bottom: calc(6rem / 16);
  }
  .title--main + p {
    margin-top: 0;
  }
  .map--link{
    margin: 0;
  }
  ${media.maxWidth(sizes.tabletWide - 1)`
    ${({mobileOrderSecond}) => mobileOrderSecond && `
      border-top: 1px solid ${colors.BORDER_COLOR};
      padding-top: calc(140rem / 16);
      order: 2;
    `}
  `}
`

export const PageContent = styled.section`
  padding: 1rem calc(24rem / 16) 2rem;
  width: 100%;
  flex-basis: 100%;
  .stretch{
    margin-left: calc(-24rem / 16);
    margin-right: calc(-24rem / 16);
  }
  .stretch--right{
    margin-right: calc(-24rem / 16);
  }
  ${media.minWidth(sizes.tabletWide)`
    padding: 1rem calc(38rem / 16);
    width: calc(100% - (300rem / 16));
    flex-basis: calc(100% - (300rem / 16));
    .stretch{
      margin-left: calc(-38rem / 16);
      margin-right: calc(-38rem / 16);
    }
    .stretch--right, .stretch--right-large{
      margin-right: calc(-38rem / 16);
    }
  `}
  .product--wrap, .tab--wrap{
    padding-top: calc(30rem / 16);
    &.small-top-gap{
      padding-top: calc(13rem / 16);
    }
  }
`

export const Container = styled.div`
  max-width: 1120px;
  margin: auto;
  padding-left: 10px;
  padding-right: 10px;
`

export const SliderContainer = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: auto;
  ${media.minWidth(sizes.tabletNarrow)`
    padding-left: 1rem;
    padding-right: 1rem;
  `}
  ${({multi}) => multi && `
    padding-left: 1rem;
    padding-right: 1rem;
  `}
  ${({topMargin}) => topMargin && `
    margin-top: 3rem;
  `}
`

export const GridContainer = styled.div`
  width: 100%;
  max-width: 1120px;
  margin: auto;
  padding-left: 2rem;
  padding-right: 2rem;
  ${({fluid}) => fluid && `
    max-width: none;
  `}
  ${({noRightPadding}) => noRightPadding && `
    padding-right: 0;
  `}
`

export const GridRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  margin-left: -1rem;
  margin-right: -1rem;
  ${({noRightPadding}) => noRightPadding && `
    margin-right: 0;
  `}
`

export const GridCol = styled.div`
  padding-left: ${BASE_CONF.gutterWidth}rem;
  padding-right: ${BASE_CONF.gutterWidth}rem;
  
  ${p => Object.keys(p)
    .filter(k => ~DIMENSION_NAMES.indexOf(k))
    .sort((k1, k2) => DIMENSION_NAMES.indexOf(k1) - DIMENSION_NAMES.indexOf(k2))
    .map(k => config(p).media[k]`${
      isInteger(p[k])
      // Integer value
        ? `
        flex-basis: ${100 / config(p).gridSize * p[k]}%;
        max-width: ${100 / config(p).gridSize * p[k]}%;
        display: block;
      `
        // Boolean
        : (p[k]
        // Auto-width
          ? `
          flex-grow: 1;
          flex-basis: 0;
          max-width: 100%;
          display: block;
        `
          // Hide element
          : 'display: none;'
        )
    }`)
  }

   ${p => Object.keys(p)
    .filter(k => ~offsetProps.indexOf(k))
    .map(k =>
      config(p).media[k.replace(/Offset$/, '')]`
        margin-left: ${100 / config(p).gridSize * p[k]}%;
      `
    )
  }

  ${({searchResult}) => searchResult && `
      @media ${`(max-width: ${BASE_CONF.breakpoints.md}em)`} {
        min-height: 50vh;
      }
  `}
  ${({noRightPadding}) => noRightPadding && `
      padding-right: 0;
  `}
`

// generic row, also handles pro page content
export const Row = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  .card-grid__item{
    max-width: unset;
    flex-basis: 100%;
    margin-bottom: calc(24rem / 16) !important;
  }
  ${props => props.eventList && `
    justify-content: space-between;
    .no-results{
      text-align: center;
      padding: calc(38rem / 16);
      width: 100%;
    }
  `}
  ${props => props.cardList && `
    justify-content: space-between;
  `}
  ${media.minWidth(sizes.tabletWide)`
    ${props => props.eventList &&
      `
        margin-left: calc(-38rem / 16);
        margin-right: calc(-38rem / 16);
      `
    }
  `}
  ${media.minWidth(sizes.tabletWide)`
    ${props => props.cardList &&
      `
        margin-left: calc(-38rem / 16);
        margin-right: calc(-38rem / 16);
      `
    }
  `}
  ${media.minWidth(sizes.tabletNarrow - 150)`{
      .card-grid__item{
        max-width: calc(50% - (12rem / 16));
        flex-basis: calc(50% - (12rem / 16));
      }
    }`
  }
  ${media.minWidth(sizes.tabletNarrow+1)`{
      .card-grid__item{
        max-width: calc(50% - (12rem / 16));
        flex-basis: calc(50% - (12rem / 16));
      }
    }`
  }

  ${media.minWidth(sizes.tabletWide)`{
      .card-grid__item{
        margin-left: calc(38rem / 16);
        max-width: calc(50% - (38rem / 16));
        flex-basis: calc(50% - (38rem / 16));
        margin-bottom: calc(38rem / 16) !important;
      }
    }`
  }
`
export const ProCalendarWrap = styled.div`
  padding-bottom: calc(14rem / 16);
  ${media.minWidth(sizes.tabletWide)`
    margin-right: calc(-38rem / 16);
  `}
`



// WEBPACK FOOTER //
// ./src/modules/common/components/grid/index.js